import { useTranslation } from 'react-i18next';
import type { MediaScaleMode } from '@ghostscreen/contracts';

const OPTIONS: readonly MediaScaleMode[] = ['fit', 'fill'];

type DisplayScaleModeControlProps = {
  value: MediaScaleMode;
  disabled?: boolean;
  saving?: boolean;
  onChange: (mode: MediaScaleMode) => void;
};

/**
 * Operator control for how playlist media paints on this display.
 * Mirrors Fit / Fill scale modes used by commercial digital signage players.
 */
export function DisplayScaleModeControl({
  value,
  disabled = false,
  saving = false,
  onChange,
}: DisplayScaleModeControlProps) {
  const { t } = useTranslation('displays');
  return (
    <fieldset className="m-0 min-w-0 border-0 p-0">
      <legend className="mb-2 text-xs font-semibold text-muted">{t('scale.legend')}</legend>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('scale.legend')}>
        {OPTIONS.map((option) => {
          const selected = value === option;
          return (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={disabled || saving}
              title={t(`scale.options.${option}.hint`)}
              onClick={() => {
                if (!selected) onChange(option);
              }}
              className={[
                'rounded-[10px] border px-4 py-2.5 text-sm font-semibold disabled:opacity-50',
                selected
                  ? 'border-signal bg-panel2 text-signal'
                  : 'border-line text-ink hover:border-[var(--gs-accent)]',
              ].join(' ')}
            >
              {t(`scale.options.${option}.label`)}
            </button>
          );
        })}
      </div>
      <p className="m-0 mt-2 text-xs text-muted">{t(`scale.options.${value}.hint`)}</p>
    </fieldset>
  );
}
